import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n from '../i18n';
import { UI_TO_URL_PREFIX, SUPPORTED_PREFIXES } from '../i18n/bootstrap';
import ko from 'react-phone-number-input/locale/ko.json';
import en from 'react-phone-number-input/locale/en.json';
import zhCN from 'react-phone-number-input/locale/zh.json';
import ja from 'react-phone-number-input/locale/ja.json';
import es from 'react-phone-number-input/locale/es.json';

const PHONE_LOCALES = { KOR: ko, ENG: en, CHN: zhCN, JPN: ja, SPA: es };

/** UI 언어 코드 → BE/카카오/DeepL 언어 코드 매핑 */
export const BE_LANG_MAP = {
  KOR: 'ko',
  ENG: 'en',
  CHN: 'zh',
  JPN: 'ja',
  SPA: 'es',
};

const STORAGE_KEY = 'vvt_lang';

function readStoredLang() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || 'KOR';
    if (raw === 'ESP') {
      localStorage.setItem(STORAGE_KEY, 'SPA');
      return 'SPA';
    }
    return raw;
  } catch {
    return 'KOR';
  }
}

/** localStorage에서 BE 언어코드 직접 읽기 (axios interceptor 등 컨텍스트 밖에서 사용) */
export function getCurrentBeLang() {
  const ui = readStoredLang();
  return BE_LANG_MAP[ui] || 'ko';
}

const LanguageContext = createContext({
  lang: 'KOR',
  beLang: 'ko',
  setLang: () => {},
  phoneLocale: ko,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  const setLang = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {}
    // i18next도 동기화 (페이지 이동 전에 미리 변경)
    const beNext = BE_LANG_MAP[next] || 'ko';
    i18n.changeLanguage(beNext);
    // SEO: 현재 URL의 언어 prefix를 제거하고 새 언어 prefix로 교체 → 다국어 indexable URL 유지.
    // ex) /en/transfer/airport (English) → /ja/transfer/airport (Japanese)
    // 한국어(KOR)는 prefix 없음 → 기존 URL과 호환.
    const newPrefix = UI_TO_URL_PREFIX[next] || '';
    let path = window.location.pathname;
    for (const p of SUPPORTED_PREFIXES) {
      const seg = `/${p}`;
      if (path === seg || path.startsWith(`${seg}/`)) {
        path = path.slice(seg.length) || '/';
        break;
      }
    }
    const newUrl = `${newPrefix}${path}${window.location.search}${window.location.hash}`;
    // 전체 reload로 모든 컴포넌트가 새 언어로 데이터 재요청
    window.location.href = newUrl;
  }, []);

  // 다른 탭에서 언어 변경 시 동기화
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && e.newValue !== lang) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [lang]);

  const beLang = BE_LANG_MAP[lang] || 'ko';

  return (
    <LanguageContext.Provider value={{ lang, beLang, setLang, phoneLocale: PHONE_LOCALES[lang] || ko }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
